/* Fernwood theme behaviour. Vanilla, no dependencies, safe to run twice (editor reloads). */
(() => {
  const root = document.documentElement;
  root.classList.add('fw-js');
  window.fwReady = true;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Scroll reveal ---------- */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              entry.target.classList.add('is-in');
              revealObserver.unobserve(entry.target);
            }
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
        )
      : null;

  // Items inside a horizontal scroller (rails, swipe rows) show at once: fading them in mid-swipe
  // left blank cards on 375, and they never reveal at all if the row is never swiped.
  function inHorizontalScroller(el) {
    for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
      if (p.scrollWidth > p.clientWidth + 1 && /(auto|scroll)/.test(getComputedStyle(p).overflowX)) return true;
    }
    return false;
  }

  function initReveal(scope) {
    const items = scope.querySelectorAll('[data-fw-reveal]:not(.is-in)');
    if (!revealObserver || reduceMotion.matches) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    items.forEach((el) => {
      if (inHorizontalScroller(el)) el.classList.add('is-in');
      else revealObserver.observe(el);
    });
  }

  /* ---------- Magnetic pull on the Drop CTA (fine pointers, full motion only) ---------- */
  // Uses the individual `translate` property so the CSS :active scale and hover wipe still apply.
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  function initMagnet(scope) {
    scope.querySelectorAll('.fw-drop .fw-btn').forEach((btn) => {
      if (btn.dataset.fwMagnet) return;
      btn.dataset.fwMagnet = '1';
      const MAX = 6;
      let frame = 0;
      btn.addEventListener('pointermove', (event) => {
        if (!finePointer.matches || reduceMotion.matches || frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          const r = btn.getBoundingClientRect();
          const dx = ((event.clientX - r.left) / r.width - 0.5) * 2 * MAX;
          const dy = ((event.clientY - r.top) / r.height - 0.5) * 2 * (MAX / 2);
          btn.style.translate = `${dx.toFixed(1)}px ${dy.toFixed(1)}px`;
        });
      });
      btn.addEventListener('pointerleave', () => {
        window.cancelAnimationFrame(frame);
        frame = 0;
        btn.style.translate = '';
      });
    });
  }

  /* ---------- Marquee pause control ---------- */
  function initMarquee(scope) {
    scope.querySelectorAll('[data-fw-marquee-toggle]').forEach((btn) => {
      if (btn.dataset.fwBound) return;
      btn.dataset.fwBound = '1';
      const bar = btn.closest('[data-fw-marquee]');
      const label = btn.querySelector('.fw-visually-hidden');
      btn.addEventListener('click', () => {
        const paused = bar.classList.toggle('is-paused');
        btn.setAttribute('aria-pressed', String(paused));
        if (label) label.textContent = paused ? 'Play announcements' : 'Pause announcements';
      });
    });
  }

  /* ---------- Product rails ---------- */
  function initRails(scope) {
    scope.querySelectorAll('[data-fw-rail]').forEach((rail) => {
      if (rail.dataset.fwBound) return;
      rail.dataset.fwBound = '1';
      const tracks = [...rail.querySelectorAll('.fw-rail__track')];
      const prev = rail.querySelector('[data-fw-rail-prev]');
      const next = rail.querySelector('[data-fw-rail-next]');
      if (!tracks.length || !prev || !next) return;
      // A tabbed rail (fw-price-tabs) scrolls whichever panel is showing.
      const active = () => tracks.find((t) => !t.closest('[hidden]')) || tracks[0];
      const step = () => Math.max(active().clientWidth * 0.8, 240);
      const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');
      const update = () => {
        const track = active();
        const max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
        rail.classList.toggle('has-overflow', max > 2);
        if (track.id) {
          prev.setAttribute('aria-controls', track.id);
          next.setAttribute('aria-controls', track.id);
        }
      };
      prev.addEventListener('click', () => active().scrollBy({ left: -step(), behavior: behavior() }));
      next.addEventListener('click', () => active().scrollBy({ left: step(), behavior: behavior() }));
      tracks.forEach((track) => {
        track.addEventListener('scroll', update, { passive: true });
        if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
      });
      rail.addEventListener('fw:tab-change', update);
      update();
    });
  }

  /* ---------- Tabbed rails: WAI-ARIA tabs, automatic activation ---------- */
  function initTabs(scope) {
    scope.querySelectorAll('[data-fw-tabs]').forEach((root) => {
      if (root.dataset.fwTabsBound) return;
      const list = root.querySelector('[data-fw-tablist]');
      if (!list) return;
      root.dataset.fwTabsBound = '1';
      const tabs = [...list.querySelectorAll('[role="tab"]')];
      const link = root.querySelector('[data-fw-tabs-link]');
      const linkLabel = root.querySelector('[data-fw-tabs-link-label]');
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        if (link && tab.dataset.fwTabUrl) link.href = tab.dataset.fwTabUrl;
        if (linkLabel) linkLabel.textContent = ': ' + tab.dataset.fwTabLabel;
        if (focus) tab.focus();
        root.dispatchEvent(new CustomEvent('fw:tab-change'));
      };
      list.hidden = false;
      root.classList.add('is-tabbed');
      select(tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0], false);
      list.addEventListener('click', (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (tab) select(tab, false);
      });
      list.addEventListener('keydown', (event) => {
        const i = tabs.indexOf(document.activeElement);
        if (i < 0) return;
        const to = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 }[event.key];
        if (to === undefined) return;
        event.preventDefault();
        select(tabs[(to + tabs.length) % tabs.length], true);
      });
    });
  }

  /* ---------- Drop countdown (only rendered when a future date is set) ---------- */
  function initCountdowns(scope) {
    scope.querySelectorAll('[data-fw-countdown]').forEach((box) => {
      if (box.dataset.fwBound) return;
      box.dataset.fwBound = '1';
      const end = Date.parse(box.dataset.fwCountdown.replace(/([+-]\d\d)(\d\d)$/, '$1:$2'));
      if (Number.isNaN(end)) {
        box.hidden = true;
        return;
      }
      const units = {
        d: box.querySelector('[data-unit="d"]'),
        h: box.querySelector('[data-unit="h"]'),
        m: box.querySelector('[data-unit="m"]'),
        s: box.querySelector('[data-unit="s"]'),
      };
      const pad = (n) => String(n).padStart(2, '0');
      let timer = 0;
      const tick = () => {
        const left = end - Date.now();
        if (left <= 0) {
          box.hidden = true;
          window.clearInterval(timer);
          return;
        }
        const s = Math.floor(left / 1000);
        units.d.textContent = pad(Math.floor(s / 86400));
        units.h.textContent = pad(Math.floor((s % 86400) / 3600));
        units.m.textContent = pad(Math.floor((s % 3600) / 60));
        units.s.textContent = pad(s % 60);
      };
      tick();
      timer = window.setInterval(tick, 1000);
    });
  }

  /* ---------- Shop the Fit hotspots ---------- */
  function closeHotspots(except) {
    document.querySelectorAll('.fw-hotspot__dot[aria-expanded="true"]').forEach((dot) => {
      if (dot === except) return;
      dot.setAttribute('aria-expanded', 'false');
      const card = document.getElementById(dot.getAttribute('aria-controls'));
      if (card) card.hidden = true;
    });
  }

  function initHotspots(scope) {
    scope.querySelectorAll('.fw-hotspot__dot').forEach((dot) => {
      if (dot.dataset.fwBound) return;
      dot.dataset.fwBound = '1';
      dot.addEventListener('click', (event) => {
        event.stopPropagation();
        const card = document.getElementById(dot.getAttribute('aria-controls'));
        const open = dot.getAttribute('aria-expanded') !== 'true';
        closeHotspots(dot);
        dot.setAttribute('aria-expanded', String(open));
        if (card) card.hidden = !open;
      });
    });
  }

  document.addEventListener('click', (event) => {
    if (!event.target.closest('.fw-hotspot')) closeHotspots();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('.fw-hotspot__dot[aria-expanded="true"]');
    if (!open) return;
    closeHotspots();
    open.focus();
  });

  /* ---------- Wishlist (localStorage "fw:wishlist", per device) ---------- */
  const WISH_KEY = 'fw:wishlist';
  const LEGACY_WISH_KEY = 'fw-wishlist';
  const cleanList = (value) =>
    Array.isArray(value) ? [...new Set(value.filter((h) => typeof h === 'string' && h.length < 256))] : [];
  const readWish = () => {
    try {
      const raw = window.localStorage.getItem(WISH_KEY) ?? window.localStorage.getItem(LEGACY_WISH_KEY);
      return cleanList(JSON.parse(raw || '[]'));
    } catch (error) {
      return [];
    }
  };
  const writeWish = (list) => {
    try {
      window.localStorage.setItem(WISH_KEY, JSON.stringify(list));
      window.localStorage.removeItem(LEGACY_WISH_KEY);
    } catch (error) {
      /* storage blocked (private mode): the toggle still works for this page view */
    }
  };

  let wishLive = null;
  function announce(text) {
    if (!wishLive) {
      wishLive = document.createElement('p');
      wishLive.className = 'fw-visually-hidden';
      wishLive.setAttribute('role', 'status');
      wishLive.setAttribute('aria-live', 'polite');
      document.body.append(wishLive);
    }
    wishLive.textContent = '';
    window.setTimeout(() => {
      wishLive.textContent = text;
    }, 60);
  }

  function renderWishCount() {
    const count = readWish().length;
    const badge = document.getElementById('fw-wishlist-count');
    if (!badge) return;
    badge.textContent = count > 99 ? '99+' : String(count);
    badge.hidden = count === 0;
    const link = badge.closest('a');
    if (link) link.setAttribute('aria-label', count ? `Wishlist, ${count} saved` : 'Wishlist');
  }

  function syncWish(scope = document) {
    const saved = new Set(readWish());
    scope.querySelectorAll('[data-fw-wish]').forEach((btn) => {
      const on = saved.has(btn.dataset.fwWish);
      if (btn.getAttribute('aria-pressed') !== String(on)) btn.setAttribute('aria-pressed', String(on));
    });
    renderWishCount();
  }

  /** Public API so the wishlist page and quick view share one source of truth. */
  window.fwWishlist = {
    list: readWish,
    has: (handle) => readWish().includes(handle),
    set(handle, on) {
      const list = readWish().filter((h) => h !== handle);
      if (on) list.unshift(handle);
      writeWish(list);
      syncWish();
      document.dispatchEvent(new CustomEvent('fw:wishlist-change', { detail: { handle, saved: on, list } }));
    },
  };

  function initWish(scope) {
    syncWish(scope);
  }

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-fw-wish]');
    if (!btn) return;
    event.preventDefault();
    const handle = btn.dataset.fwWish;
    const on = !window.fwWishlist.has(handle);
    window.fwWishlist.set(handle, on);
    const card = btn.closest('[aria-labelledby]');
    const title = card ? document.getElementById(card.getAttribute('aria-labelledby'))?.textContent.trim() : '';
    announce(on ? `Saved ${title || 'item'} to your wishlist` : `Removed ${title || 'item'} from your wishlist`);
    if (on && !reduceMotion.matches) {
      btn.classList.remove('is-popping');
      void btn.offsetWidth;
      btn.classList.add('is-popping');
      btn.addEventListener('animationend', () => btn.classList.remove('is-popping'), { once: true });
    }
  });

  /* Cards added later (filters, pagination, recommendations, quick view) pick up saved state. */
  if ('MutationObserver' in window) {
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        syncWish();
        // build C: re-rendered cards and rails get the colour preview, peek and drag too (idempotent)
        initSwatchPreview(document);
        initPeek(document);
        initDragRails(document);
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ---------- Drop-alert form: inline validation before posting ---------- */
  function initSignup(scope) {
    scope.querySelectorAll('.fw-signup__form').forEach((form) => {
      if (form.dataset.fwBound) return;
      form.dataset.fwBound = '1';
      const input = form.querySelector('input[type="email"]');
      const error = form.querySelector('[data-fw-signup-error]');
      if (!input || !error) return;
      const valid = () => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(input.value.trim());
      form.addEventListener('submit', (event) => {
        if (valid()) return;
        event.preventDefault();
        input.setAttribute('aria-invalid', 'true');
        error.hidden = false;
        input.focus();
      });
      input.addEventListener('input', () => {
        if (!valid()) return;
        input.removeAttribute('aria-invalid');
        error.hidden = true;
      });
    });
  }

  /* ---------- Card colour preview (build C) ----------
     Hover or focus a colour dot to preview that colour's photo; click pins it (aria-pressed) and points
     the card links at that variant. Leaving the dots without a pin restores the original photo. */
  function initSwatchPreview(scope) {
    scope.querySelectorAll('.fw-card').forEach((card) => {
      const buttons = [...card.querySelectorAll('[data-fw-swatch]')];
      if (!buttons.length || card.dataset.fwSwatchBound) return;
      card.dataset.fwSwatchBound = '1';
      const img = card.querySelector('.fw-card__img:not(.fw-card__img--alt)');
      const links = [...card.querySelectorAll('.fw-card__link, .fw-card__image-link')];
      if (!img) return;
      const original = { src: img.src, srcset: img.srcset, hrefs: links.map((a) => a.href) };
      let pinned = null;
      const show = (btn) => {
        card.classList.add('is-swatching');
        if (btn) {
          img.srcset = btn.dataset.fwSwatchSrcset || '';
          img.src = btn.dataset.fwSwatch;
        } else {
          img.srcset = original.srcset;
          img.src = original.src;
        }
        if (!btn) card.classList.remove('is-swatching');
      };
      const list = buttons[0].closest('.fw-card__dots');
      buttons.forEach((btn) => {
        btn.addEventListener('pointerenter', () => finePointer.matches && show(btn));
        btn.addEventListener('focus', () => show(btn));
        btn.addEventListener('click', () => {
          pinned = pinned === btn ? null : btn;
          buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === pinned)));
          links.forEach((a, i) => {
            a.href = pinned ? pinned.dataset.fwSwatchUrl : original.hrefs[i];
          });
          show(pinned);
        });
      });
      list.addEventListener('pointerleave', () => show(pinned));
      list.addEventListener('focusout', (event) => {
        if (!list.contains(event.relatedTarget)) show(pinned);
      });
    });
  }

  /* ---------- Long-press peek on touch (build C) ----------
     Press and hold a card photo for 350 ms to see the second photo; lift to go back. A drag cancels it
     (scrolling wins), and the click that ends a peek does not open the product. */
  const coarsePointer = window.matchMedia('(hover: none) and (pointer: coarse)');
  function initPeek(scope) {
    scope.querySelectorAll('.fw-card__media--swap').forEach((mediaEl) => {
      if (mediaEl.dataset.fwPeekBound) return;
      mediaEl.dataset.fwPeekBound = '1';
      const card = mediaEl.closest('.fw-card');
      let timer = 0;
      let startX = 0;
      let startY = 0;
      let peeked = false;
      const end = () => {
        window.clearTimeout(timer);
        timer = 0;
        if (peeked) card.classList.remove('is-peek');
      };
      mediaEl.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch' || !coarsePointer.matches) return;
        if (event.target.closest('button')) return;
        peeked = false;
        startX = event.clientX;
        startY = event.clientY;
        timer = window.setTimeout(() => {
          peeked = true;
          card.classList.add('is-peek');
        }, 350);
      });
      mediaEl.addEventListener('pointermove', (event) => {
        if (timer && Math.hypot(event.clientX - startX, event.clientY - startY) > 8) end();
      });
      ['pointerup', 'pointercancel', 'pointerleave'].forEach((type) => mediaEl.addEventListener(type, end));
      mediaEl.addEventListener('contextmenu', (event) => {
        if (peeked || timer) event.preventDefault();
      });
      mediaEl.addEventListener(
        'click',
        (event) => {
          if (!peeked) return;
          peeked = false;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
    });
  }

  /* ---------- Drag to scroll product rails with a mouse (build C) ----------
     Touch already swipes natively and keyboard uses the arrow buttons or the focused track.
     A drag longer than 6 px swallows the click that ends it, so dragging never opens a product. */
  function initDragRails(scope) {
    scope.querySelectorAll('.fw-rail__track, [data-fw-drag]').forEach((track) => {
      if (track.dataset.fwDragBound) return;
      track.dataset.fwDragBound = '1';
      let startX = 0;
      let startLeft = 0;
      let moved = 0;
      let active = false;
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (event.target.closest('button, input, select, label, textarea')) return;
        if (track.scrollWidth <= track.clientWidth + 1) return;
        active = true;
        moved = 0;
        startX = event.clientX;
        startLeft = track.scrollLeft;
      });
      track.addEventListener('pointermove', (event) => {
        if (!active) return;
        const dx = event.clientX - startX;
        if (!moved && Math.abs(dx) < 6) return;
        if (!moved) {
          track.setPointerCapture(event.pointerId);
          track.classList.add('is-dragging');
        }
        moved = Math.abs(dx);
        track.scrollLeft = startLeft - dx;
      });
      const release = (event) => {
        if (!active) return;
        active = false;
        if (track.hasPointerCapture?.(event.pointerId)) track.releasePointerCapture(event.pointerId);
        track.classList.remove('is-dragging');
      };
      track.addEventListener('pointerup', release);
      track.addEventListener('pointercancel', release);
      track.addEventListener('dragstart', (event) => event.preventDefault());
      track.addEventListener(
        'click',
        (event) => {
          if (moved < 6) return;
          moved = 0;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
    });
  }

  /* ---------- Tilt on the campus edit cards (build C; fine pointers, full motion only) ---------- */
  function initTilt(scope) {
    scope.querySelectorAll('.fw-edits__link, [data-fw-tilt]').forEach((el) => {
      if (el.dataset.fwTiltBound) return;
      el.dataset.fwTiltBound = '1';
      const MAX = 5; // degrees
      let frame = 0;
      el.addEventListener('pointermove', (event) => {
        if (event.pointerType !== 'mouse' || !finePointer.matches || reduceMotion.matches || frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          const r = el.getBoundingClientRect();
          const x = (event.clientX - r.left) / r.width - 0.5;
          const y = (event.clientY - r.top) / r.height - 0.5;
          el.classList.add('is-tilting');
          el.style.transform = `perspective(900px) rotateX(${(-y * 2 * MAX).toFixed(2)}deg) rotateY(${(x * 2 * MAX).toFixed(2)}deg)`;
          el.style.setProperty('--fw-tilt-x', `${((x + 0.5) * 100).toFixed(1)}%`);
          el.style.setProperty('--fw-tilt-y', `${((y + 0.5) * 100).toFixed(1)}%`);
        });
      });
      el.addEventListener('pointerleave', () => {
        window.cancelAnimationFrame(frame);
        frame = 0;
        el.classList.remove('is-tilting');
        el.style.transform = '';
      });
    });
  }

  function init(scope = document) {
    initSwatchPreview(scope);
    initPeek(scope);
    initDragRails(scope);
    initTilt(scope);
    initSignup(scope);
    initReveal(scope);
    initMagnet(scope);
    initMarquee(scope);
    initTabs(scope);
    initRails(scope);
    initCountdowns(scope);
    initHotspots(scope);
    initWish(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  window.addEventListener('storage', (event) => {
    if (event.key === WISH_KEY) syncWish();
  });
})();
